import { InstrumentId } from '../types/assessment'

// Per-instrument role vocabularies. A posting resolves to whichever instrument's
// vocabulary it matches; `library` lists the terms that ARE that instrument's
// own role, as opposed to an adjacent one.
const VOCAB: Record<InstrumentId, { keywords: string[]; library: string[] }> = {
  'FIN-001': {
    keywords: [
      'group-financial-controller',
      'financial-controller',
      'finance-controller',
      'group-controller',
      'management-accountant',
      'financial-accountant',
      'financial-analyst',
      'group-accountant',
      'staff-accountant',
      'tax-accountant',
      'internal-auditor',
      'regulatory-reporting',
      'finance-manager',
      'finance-director',
      'credit-risk-analyst',
      'credit-analyst',
      'finance-business-partner',
      'accountant',
      'controller',
      'fp-a',
      'fpa',
    ],
    library: [
      'group-financial-controller',
      'financial-controller',
      'finance-controller',
      'group-controller',
      'controller',
    ],
  },
  'LAW-001': {
    keywords: [
      'trainee-solicitor',
      'solicitor-trainee',
      'corporate-trainee',
      'training-contract',
      'newly-qualified-solicitor',
      'nq-solicitor',
      'corporate-associate',
      'commercial-solicitor',
      'corporate-solicitor',
      'corporate-lawyer',
      'commercial-lawyer',
      'legal-counsel',
      'general-counsel',
      'company-secretary',
      'legal-assistant',
      'paralegal',
      'solicitor',
      'lawyer',
      'barrister',
      'conveyancer',
    ],
    library: [
      'trainee-solicitor',
      'solicitor-trainee',
      'corporate-trainee',
      'training-contract',
    ],
  },
}

export const INSTRUMENT_IDS = Object.keys(VOCAB) as InstrumentId[]

// Every keyword across every instrument, longest first so the most specific wins.
const ALL_KEYWORDS: { keyword: string; instrument: InstrumentId }[] = INSTRUMENT_IDS.flatMap(
  (instrument) => VOCAB[instrument].keywords.map((keyword) => ({ keyword, instrument })),
).sort((a, b) => b.keyword.length - a.keyword.length)

export const ROLE_KEYWORDS = ALL_KEYWORDS.map((k) => k.keyword)

export function stripQuery(url: string) {
  return url.split('?')[0].split('#')[0]
}

const titleCase = (slug: string) =>
  slug
    .split(/[-_+\s]/)
    .filter(Boolean)
    .map((t) =>
      t === 'fpa' || t === 'fp' || t === 'a' || t === 'nq'
        ? t.toUpperCase()
        : t.charAt(0).toUpperCase() + t.slice(1),
    )
    .join(' ')

// Postings pasted as free text use spaces rather than hyphens.
const normalise = (raw: string) => stripQuery(raw).toLowerCase().replace(/[\s_/]+/g, '-')

function matchKeyword(raw: string) {
  const path = normalise(raw)
  return ALL_KEYWORDS.find((k) => path.includes(k.keyword)) || null
}

export interface UrlSignal {
  matched: boolean
  token: string
}

export function readUrlSignal(url: string): UrlSignal {
  const match = matchKeyword(url)
  if (!match) return { matched: false, token: 'no role keyword in URL' }
  return { matched: true, token: `/${match.keyword}/` }
}

export interface RoleMatch {
  urlRole: string | null
  adjacent: boolean
  instrument: InstrumentId | null
}

export function readRoleMatch(url: string): RoleMatch {
  const match = matchKeyword(url)
  if (!match) return { urlRole: null, adjacent: false, instrument: null }
  return {
    urlRole: titleCase(match.keyword),
    adjacent: !VOCAB[match.instrument].library.includes(match.keyword),
    instrument: match.instrument,
  }
}

// Which instrument a posting resolves to. Checks the URL first, then any pasted
// body text. Returns null when nothing in the library matches.
export function resolveInstrument(url: string, text = ''): InstrumentId | null {
  return matchKeyword(url)?.instrument ?? matchKeyword(text)?.instrument ?? null
}
